import { _js, ___p } from '@ifixit/localize';
import { type CartLineItem, useAddToCart } from '@ifixit/shopify-cart-sdk';
import {
   HStack,
   VStack,
   Image,
   Button,
   Text,
   ImageProps,
   ButtonProps,
   StackProps,
   useBreakpoint,
   Box,
   ThemingProps,
   Link,
   LinkProps,
   Flex,
   useDimensions,
   Badge,
   useBreakpointValue,
   Select,
} from '@chakra-ui/react';
import {
   Dispatch,
   Fragment,
   MutableRefObject,
   SetStateAction,
   useLayoutEffect,
   useRef,
} from 'react';
import { fireGlobal } from 'Shared/utils';
import { QuantityBadge } from './QuantityBadge';
import { PlaceholderImage } from './PlaceholderImage';
import trackInPiwikAndGA from 'Shared/Analytics/CombinedGAPiwik';
import { useHistoryState } from './HistoryState';
import { FaIcon } from '@ifixit/icons';
import { faCircleInfo, faLink } from '@fortawesome/pro-solid-svg-icons';
import { useState } from 'react';

export interface GuideProductsData {
   kits: GuideProduct[];
   parts: GuideProduct[];
   tools: GuideProduct[];
   hasAffiliateLink: boolean;
}

interface GuideProductVariant {
   name: string;
   totalPrice: string;
   cartLine: CartLineItem;
   isInStock: boolean;
}

export interface GuideProduct {
   name: string;
   sku: string | null;
   totalPrice: string | null;
   quantity: number;
   notes: string | null;
   type: string | null;
   url: string;
   wikiUrl: string;
   imageUrl: string | null;
   highResImageUrl: string | null;
   isOptional: boolean;
   isExternal: boolean;
   isForSale: boolean;
   supplierText: string | null;
   shopifyHandle: string | null;
   variants: GuideProductVariant[];
   initialVariant: GuideProductVariant | null;
}
type PartOrTool = 'Part' | 'Tool' | 'Kit';

function ProductItem({
   name,
   sku,
   quantity,
   notes,
   type,
   url,
   imageUrl,
   highResImageUrl,
   isExternal,
   isForSale,
   supplierText,
   shopifyHandle,
   variants,
   initialVariant,
   addToCartText,
   viewProductText,
   wrapperStyling,
   imageStyling,
   priceStyling,
   buttonStyling,
   partOrTool,
   isOptional,
}: GuideProduct & {
   addToCartText: string;
   viewProductText: string;
   wrapperStyling: StackProps;
   imageStyling: ImageProps;
   priceStyling: LinkProps;
   buttonStyling: ButtonProps;
   partOrTool: PartOrTool;
}) {
   const openInNewTab = true;
   const breakpoint = useBreakpoint({ ssr: false });
   const isTablet = breakpoint == 'sm' || breakpoint == 'base';
   const [selectedVariant, setSelectedVariant] = useState<GuideProductVariant | null>(
      initialVariant
   );

   const title = type ? `${name} (${type})` : name;
   const escapedDashTitle = title.replace('-', '\u2011');

   const onProductClick = () => {
      trackInPiwikAndGA({
         eventCategory: 'Guide Products',
         eventAction: `Guide Products - Click Product - ${partOrTool}${
            isExternal ? ' - External' : ''
         }`,
         eventName: isExternal ? `external:${name}:${url}` : `ifixit:${name}:${sku}`,
      });
   };

   return (
      <HStack
         width="100%"
         as="li"
         padding="12px"
         {...wrapperStyling}
         flexShrink={0}
         onClick={onProductClick}
      >
         <Box position="relative" marginRight={1.5} display="flex" alignSelf="flex-start">
            <Link
               href={url}
               isExternal={openInNewTab}
               alignSelf="flex-start"
               display="flex"
               _visited={{ color: 'gray.700' }}
               sx={{
                  '&:hover img': {
                     borderWidth: '1px',
                     borderRadius: '4px',
                  },
               }}
            >
               {imageUrl ? (
                  <Image
                     alignSelf="flex-start"
                     borderWidth={1}
                     borderRadius="4px"
                     display="block"
                     src={imageUrl}
                     srcSet={`${imageUrl} 1x, ${highResImageUrl} 2x`}
                     objectFit="cover"
                     alt={name}
                     {...imageStyling}
                  />
               ) : (
                  <PlaceholderImage {...imageStyling} />
               )}
               {quantity > 1 && <QuantityBadge quantity={quantity} />}
            </Link>
         </Box>
         <VStack spacing="1" flex={1} alignItems="flex-start">
            <Link
               fontSize="md"
               fontWeight="semibold"
               href={url}
               isExternal={openInNewTab}
               className="title"
               color="gray.900"
               _visited={{ color: 'gray.900' }}
               _hover={{ color: 'gray.700', textDecoration: 'none' }}
               _focus={{ color: 'gray.700', textDecoration: 'none' }}
            >
               {escapedDashTitle}
            </Link>
            <VariantSelector
               variants={variants}
               selected={selectedVariant}
               setSelected={setSelectedVariant}
            />
            {selectedVariant?.totalPrice && isForSale && variants.length < 2 && (
               <Link
                  fontSize="sm"
                  fontWeight="medium"
                  href={url}
                  isExternal={openInNewTab}
                  _visited={{ color: 'gray.700' }}
                  {...priceStyling}
               >
                  {selectedVariant.totalPrice}
               </Link>
            )}
            <ProductBadges
               isOptional={isOptional}
               isExternal={isExternal}
               supplierText={supplierText}
               url={url}
               openInNewTab={openInNewTab}
            />
            {isExternal && supplierText && (
               <Link
                  fontSize="md"
                  color="amber.600"
                  href={url}
                  isExternal={openInNewTab}
                  _visited={{ color: 'amber.600' }}
               >
                  {supplierText}
               </Link>
            )}
            {notes && (
               <Link
                  fontSize="sm"
                  href={url}
                  isExternal={openInNewTab}
                  _visited={{ color: 'gray.700' }}
               >
                  {notes}
               </Link>
            )}
            {isTablet && (
               <BuyButton
                  buttonSize="sm"
                  isForSale={isForSale}
                  isExternal={isExternal}
                  openInNewTab={openInNewTab}
                  url={url}
                  buttonStyling={buttonStyling}
                  addToCartText={addToCartText}
                  viewProductText={viewProductText}
                  partOrTool={partOrTool}
                  shopifyHandle={shopifyHandle}
                  cartLine={selectedVariant?.cartLine ?? null}
                  isInStock={selectedVariant?.isInStock ?? false}
               />
            )}
         </VStack>
         {!isTablet && (
            <BuyButton
               buttonSize="md"
               isForSale={isForSale}
               isExternal={isExternal}
               openInNewTab={openInNewTab}
               url={url}
               buttonStyling={buttonStyling}
               addToCartText={addToCartText}
               viewProductText={viewProductText}
               partOrTool={partOrTool}
               shopifyHandle={shopifyHandle}
               cartLine={selectedVariant?.cartLine ?? null}
               isInStock={selectedVariant?.isInStock ?? false}
            />
         )}
      </HStack>
   );
}

function VariantSelector({
   variants,
   selected,
   setSelected,
}: {
   variants: GuideProductVariant[];
   selected: GuideProductVariant | null;
   setSelected: (_: GuideProductVariant | null) => void;
}) {
   if (variants.length < 2 || !selected) {
      return null;
   }
   const inStockVariants = variants.filter(v => v.isInStock);
   const outOfStockVariants = variants.filter(v => !v.isInStock);
   const soldOut = _js('Sold out');
   return (
      <Select
         value={selected.name}
         onChange={event => {
            const newSelection = inStockVariants.find(
               variant => variant.name === event.target.value
            );
            setSelected(newSelection ?? null);
         }}
         overflow="hidden"
         textOverflow="ellipsis"
         borderColor="gray.200"
      >
         {inStockVariants.map(variant => (
            <option key={variant.name} value={variant.name} disabled={false}>
               {variant.totalPrice} {variant.name}
            </option>
         ))}
         {outOfStockVariants.map(variant => (
            <option key={variant.name} value={variant.name} disabled={true}>
               {variant.totalPrice} {variant.name} ({soldOut})
            </option>
         ))}
      </Select>
   );
}

function ProductBadges({
   isOptional,
   isExternal,
   supplierText,
   url,
   openInNewTab,
}: {
   isOptional: boolean;
   isExternal: boolean;
   supplierText: string | null;
   url: string;
   openInNewTab: boolean;
}) {
   const hasAffiliateLink =
      isExternal &&
      supplierText &&
      (supplierText.includes('Ebay') || supplierText.includes('Amazon'));
   const hasBadge = isOptional || hasAffiliateLink;
   if (!hasBadge) {
      return null;
   }

   return (
      <Flex flexWrap="wrap" gap="1.5">
         {isOptional && (
            <ProductBadge url={url} openInNewTab={openInNewTab} hasAffiliateLink={false} />
         )}
         {hasAffiliateLink && (
            <ProductBadge url={url} openInNewTab={openInNewTab} hasAffiliateLink={true} />
         )}
      </Flex>
   );
}

function ProductBadge({
   url,
   openInNewTab,
   hasAffiliateLink,
}: {
   url: string;
   openInNewTab: boolean;
   hasAffiliateLink: boolean;
}) {
   const badgeColor = hasAffiliateLink ? 'gray' : 'amber';

   return (
      <Badge
         background={`${badgeColor}.100`}
         color={`${badgeColor}.700`}
         borderWidth={1}
         borderColor={`${badgeColor}.300`}
         display="flex"
         alignItems="center"
         fontSize="10px"
         fontWeight="semibold"
         lineHeight="12px"
         textTransform="none"
         borderRadius="md"
         padding="4px 6px"
         as={Link}
         href={url}
         isExternal={openInNewTab}
         _visited={{ color: `${badgeColor}.700` }}
      >
         <FaIcon
            icon={hasAffiliateLink ? faLink : faCircleInfo}
            color={`${badgeColor}.500`}
            paddingRight={1}
            fontSize="10px"
         />
         {hasAffiliateLink ? _js('Affiliate link') : _js('Optional')}
      </Badge>
   );
}

function GuideItemMicrodata({
   partOrTool,
   name,
   quantity,
   url,
   totalPrice,
}: Pick<GuideProduct, 'name' | 'quantity' | 'url' | 'totalPrice'> & {
   partOrTool: PartOrTool;
}) {
   if (partOrTool === 'Part' || partOrTool === 'Kit') {
      return <PartSchema name={name} quantity={quantity} url={url} totalPrice={totalPrice} />;
   }
   return <ToolSchema name={name} quantity={quantity} url={url} />;
}

function PartSchema({
   name,
   quantity,
   url,
   totalPrice,
}: Pick<GuideProduct, 'name' | 'quantity' | 'url' | 'totalPrice'>) {
   return (
      <div itemProp="supply" itemScope itemType="http://schema.org/HowToSupply">
         <meta itemProp="name" content={name} />
         <meta itemProp="requiredQuantity" content={`${quantity}`} />
         <meta itemProp="url" content={url} />
         <meta itemProp="estimatedCost" content={`${totalPrice}`} />
      </div>
   );
}

function ToolSchema({ quantity, url, name }: Pick<GuideProduct, 'name' | 'quantity' | 'url'>) {
   return (
      <div itemProp="tool" itemScope itemType="http://schema.org/HowToTool">
         <meta itemProp="name" content={name} />
         <meta itemProp="requiredQuantity" content={`${quantity}`} />
         <meta itemProp="url" content={url} />
      </div>
   );
}

function BuyButton({
   isForSale,
   url,
   buttonStyling,
   addToCartText,
   viewProductText,
   buttonSize,
   openInNewTab,
   shopifyHandle,
   cartLine,
   isInStock,
}: Pick<GuideProduct, 'isForSale' | 'url' | 'isExternal' | 'shopifyHandle'> & {
   buttonStyling?: ButtonProps;
   addToCartText: string;
   viewProductText: string;
   buttonSize: ThemingProps<'Button'>['size'];
   partOrTool: PartOrTool;
   openInNewTab: boolean;
   cartLine: CartLineItem | null;
   isInStock: boolean;
}) {
   const { addToCart, enabled } = useAddToCart({
      eventType: 'Guide Products',
   });
   if (!isForSale) {
      return null;
   }
   const openSettings = openInNewTab ? { target: '_blank', rel: 'noopener' } : {};

   if (shopifyHandle && cartLine && isInStock) {
      return (
         <Button
            isDisabled={!enabled}
            onClick={() => {
               addToCart.mutate({
                  analytics: {
                     type: 'product',
                     currentItemCode: cartLine.itemcode,
                     handle: shopifyHandle,
                     variantid: cartLine.shopifyVariantId,
                  },
                  lines: [cartLine],
               });
               fireGlobal('cart.addcomplete');
            }}
            {...openSettings}
            alignSelf={{ md: 'flex-start', sm: 'flex-end', base: 'flex-end' }}
            flexShrink={0}
            size={buttonSize}
            colorScheme="blue"
            variant="solid"
            color="white"
         >
            {addToCartText}
         </Button>
      );
   }

   return (
      <Button
         as="a"
         href={url}
         {...openSettings}
         alignSelf={{ md: 'flex-start', sm: 'flex-end', base: 'flex-end' }}
         flexShrink={0}
         size={buttonSize}
         {...buttonStyling}
      >
         {viewProductText}
      </Button>
   );
}

function FeaturedProduct(props: GuideProduct & { partOrTool: PartOrTool }) {
   const border = {
      borderTop: '1px solid',
      borderLeft: '1px solid',
      borderRight: '1px solid',
      borderColor: 'brand.200',
   };

   const styling = {
      wrapperStyling: {
         bg: 'brand.100',
         _first: { borderTopRadius: '4px', ...border },
         _notLast: border,
         _last: {
            borderBottomRadius: '4px',
            borderBottomWidth: '1px',
            ...border,
         },
      },
      imageStyling: {
         height: '64px',
         width: '64px',
      },
      priceStyling: {
         color: 'brand.600',
      },
      buttonStyling: {
         colorScheme: 'blue',
         variant: 'solid',
         color: 'white',
      },
   };
   return (
      <ProductItem
         {...props}
         {...styling}
         addToCartText={_js('Add to cart')}
         viewProductText={_js('View')}
      />
   );
}

function Product(props: GuideProduct & { partOrTool: PartOrTool }) {
   const border = {
      borderTopWidth: '1px',
      borderLeftWidth: '1px',
      borderRightWidth: '1px',
      borderColor: 'gray.200',
   };

   const styling = {
      wrapperStyling: {
         bg: 'white',
         _first: { borderTopRadius: '4px', ...border },
         _notLast: border,
         _last: {
            borderBottomRadius: '4px',
            borderBottomWidth: '1px',
            ...border,
         },
      },
      imageStyling: {
         height: '48px',
         width: '48px',
      },
      priceStyling: {},
      buttonStyling: {},
   };

   return (
      <ProductItem
         {...props}
         {...styling}
         addToCartText={props.isExternal ? _js('View') : _js('Add to cart')}
         viewProductText={_js('View')}
      />
   );
}

function ProductList({
   products,
   heading,
   subheading,
   ItemsAs,
   partOrTool,
   renderMicrodata,
}: {
   products: Array<GuideProduct>;
   heading?: string;
   subheading?: string;
   ItemsAs: (_: GuideProduct & { partOrTool: PartOrTool }) => JSX.Element;
   partOrTool: PartOrTool;
   renderMicrodata: boolean;
}) {
   const productItems = products.map((product: GuideProduct, index) => (
      <Fragment key={index}>
         <ItemsAs {...product} partOrTool={partOrTool} />
      </Fragment>
   ));

   const ItemsMicrodata = renderMicrodata
      ? products.map((product: GuideProduct, index) => (
           <GuideItemMicrodata
              key={index}
              name={product.name}
              quantity={product.quantity}
              url={product.url}
              totalPrice={product.totalPrice}
              partOrTool={partOrTool}
           />
        ))
      : null;

   return (
      <>
         {heading && (
            <Text
               color="gray.800"
               fontSize={{ base: 'lg', sm: 'xl' }}
               fontWeight="medium"
               padding="0"
               margin="0"
               _notFirst={{ paddingTop: '16px' }}
            >
               {heading}
            </Text>
         )}
         {subheading && (
            <Text fontSize="md" fontWeight="400" padding="0" margin="0" color="gray.500">
               {subheading}
            </Text>
         )}
         <Flex flexDirection="row" flexShrink={0} width="100%">
            <VStack
               as="ul"
               className={`item-list-${partOrTool.toLowerCase()}s`}
               spacing="0"
               padding="0"
               paddingTop="12px"
               width="100%"
               flexShrink={0}
               _notLast={{ paddingBottom: '12px' }}
               margin={0}
               overflowWrap="break-word"
            >
               {productItems}
            </VStack>
         </Flex>
         {ItemsMicrodata}
      </>
   );
}

export function GuideProductsSection({
   kits,
   tools,
   parts,
   hasAffiliateLink,
   introRef,
}: GuideProductsData & { introRef: MutableRefObject<HTMLDivElement> }) {
   const breakpoint = useBreakpoint({ ssr: false });
   const isMobile = breakpoint == 'base';

   const introDims = useDimensions(introRef, true);

   const disclosureTopMargin = 16;
   const disclosurePadding = 16;
   const disclosureBorder = 1;
   const disclosureFontSize = 12;

   const disclosureHeight = hasAffiliateLink
      ? disclosureTopMargin + disclosureBorder * 2 + disclosurePadding * 2 + disclosureFontSize
      : 0;

   const sectionCollapsedHeight = isMobile
      ? 264
      : // @ts-expect-error TS(2532) FIXME: Object is possibly 'undefined'.
        Math.max(introDims?.marginBox.height - disclosureHeight || 0, 370);

   const buttonHeight = 80;
   const padding = useBreakpointValue({ base: 12, md: 16 });

   const [isExpanded, setIsExpanded] = useHistoryState(false, 'productsExpanded');
   const elementRef = useRef<HTMLDivElement>();

   useLayoutEffect(() => {
      if (!elementRef.current) {
         return;
      }

      const myHeight = elementRef.current.scrollHeight;
      const expandedByDefault = myHeight < sectionCollapsedHeight + buttonHeight;
      if (expandedByDefault) {
         setIsExpanded(true);
      }
   }, [elementRef]);

   const border = { borderWidth: '1px' };

   const hasKit = kits.length > 0;

   return (
      <VStack
         spacing="0"
         padding={`${padding}px`}
         paddingBottom={isExpanded ? `${padding}px` : '0px'}
         flexGrow={0}
         alignItems="flex-start"
         {...border}
         borderRadius="4px"
         overflowY={{ base: 'hidden', md: 'auto' }}
         overflowX="hidden"
         transitionProperty="max-height"
         // @ts-expect-error TS(2322) FIXME: Type 'string | null' is not assignable to type 'Re... Remove this comment to see the full error message
         transitionDuration={isExpanded ? '300ms' : null}
         maxHeight={isExpanded ? '100%' : `${sectionCollapsedHeight}px`}
         // @ts-expect-error TS(2322) FIXME: Type 'MutableRefObject<HTMLDivElement | undefined>... Remove this comment to see the full error message
         ref={elementRef}
      >
         {kits.length && (
            <ProductList
               products={kits}
               heading={___p(kits.length, 'Fix Kit', 'Fix Kits')}
               ItemsAs={FeaturedProduct}
               partOrTool="Kit"
               subheading={___p(
                  kits.length,
                  'This kit contains all the parts and tools needed to complete this guide.',
                  'These kits contain all the parts and tools needed to complete this guide.'
               )}
               renderMicrodata={true}
            />
         )}
         {parts.length && (
            <ProductList
               products={parts}
               heading={_js('Parts')}
               ItemsAs={Product}
               partOrTool="Part"
               renderMicrodata={!hasKit}
            />
         )}
         {tools.length && (
            <ProductList
               products={tools}
               heading={_js('Tools')}
               ItemsAs={Product}
               partOrTool="Tool"
               renderMicrodata={!hasKit}
            />
         )}
         <ExpandCollapse
            // @ts-expect-error TS(2322) FIXME: Type 'number | undefined' is not assignable to typ... Remove this comment to see the full error message
            parentPadding={padding}
            buttonHeight={buttonHeight}
            isExpanded={isExpanded}
            // @ts-expect-error TS(2322) FIXME: Type 'Dispatch<boolean>' is not assignable to type... Remove this comment to see the full error message
            setIsExpanded={setIsExpanded}
         />
      </VStack>
   );
}

function ExpandCollapse({
   buttonHeight,
   parentPadding,
   isExpanded,
   setIsExpanded,
}: {
   buttonHeight: number;
   parentPadding: number;
   isExpanded: boolean;
   setIsExpanded: Dispatch<SetStateAction<boolean>>;
}) {
   const onClick = () => {
      trackInPiwikAndGA({
         eventCategory: 'Guide Products',
         eventAction: 'Guide Products - Show more',
      });
      setIsExpanded(true);
   };

   if (isExpanded) {
      return null;
   }

   return (
      <Box
         position="sticky"
         bottom="0px"
         width={`calc(100% + 2 * ${parentPadding}px)`}
         minHeight={`${buttonHeight}px`}
         borderBottomRadius="4px"
         style={{
            marginRight: `-${parentPadding}px`,
            marginLeft: `-${parentPadding}px`,
            marginTop: '1px',
         }}
         padding="0"
         background={
            'linear-gradient(182.23deg, rgba(255, 255, 255, 0) 1.87%, rgba(255, 255, 255, 0.98) 47.49%, #FFFFFF 98.13%)'
         }
         _focus={{
            textDecoration: 'underline',
         }}
         flexDirection="column"
         justifyContent="flex-end"
         display="flex"
         alignItems="center"
      >
         <Text
            fontSize="md"
            color="brand.500"
            fontWeight="medium"
            onClick={onClick}
            cursor="pointer"
            margin={0}
            textAlign="center"
            width="100%"
            paddingTop="12px"
            paddingBottom="12px"
         >
            {_js('Show more…')}
         </Text>
      </Box>
   );
}
